import { graphql } from '@/gql';

export const FRIDGE_SCANNED_QUERY = graphql(`
  query FridgeScanned($given_id: String!) {
    eatfresh_fridge(where: { given_id: { _eq: $given_id } }) {
      id
      door_status
      disabled
      connection_status
      temperature
      given_id
      place {
        ...Place
      }
    }
  }
`);

export const ADMIN_USERS_QUERY = graphql(`
  query AdminUsers(
    $limit: Int!
    $offset: Int!
    $where: eatfresh_user_account_bool_exp
    $order_by: [eatfresh_user_account_order_by!] = { last_login: desc }
  ) {
    eatfresh_user_account(
      limit: $limit
      offset: $offset
      where: $where
      order_by: $order_by
    ) {
      ...UserFragment
    }

    eatfresh_user_account_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const ADMIN_ORDERS_QUERY = graphql(`
  query AdminOrders(
    $limit: Int!
    $offset: Int!
    $order_by: [eatfresh_customer_order_order_by!] = { created: desc }
    $where: eatfresh_customer_order_bool_exp
  ) {
    eatfresh_customer_order(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      ...CustomerOrderFields
    }
    eatfresh_customer_order_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const LOGGED_IN_USER_QUERY = graphql(`
  query LoggedInUser($id: uuid!) {
    eatfresh_user_account_by_pk(id: $id) {
      ...UserFragment
    }
  }
`);

export const ADMIN_RFIDS_QUERY = graphql(`
  query AdminRfids(
    $limit: Int
    $offset: Int
    $rfid_number: String!
    $rfid_status: [eatfresh_rfid_status_enum!]!
    $article_id: uuid_comparison_exp
  ) {
    eatfresh_rfid_tag(
      where: {
        rfid_number: { _ilike: $rfid_number }
        rfid_status: { _in: $rfid_status }
        article_id: $article_id
      }
      order_by: { rfid_number: asc }
      limit: $limit
      offset: $offset
    ) {
      ...Rfid
    }
    eatfresh_rfid_tag_aggregate(
      where: {
        rfid_number: { _ilike: $rfid_number }
        rfid_status: { _in: $rfid_status }
        article_id: $article_id
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const ADMIN_ARTICLES_QUERY = graphql(`
  query AdminArticles(
    $limit: Int!
    $offset: Int!
    $order_by: [eatfresh_article_order_by!] = { name: asc }
    $where: eatfresh_article_bool_exp
  ) {
    eatfresh_article(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      ...AdminArticle
      article_detail {
        ...ArticleDetails
        wholesale_price
      }
    }
    eatfresh_article_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const ADMIN_FRIDGE_LAYOUT_ARTICLE_CANDIDATES_QUERY = graphql(`
  query AdminFridgeLayoutArticleCandidates(
    $where: eatfresh_article_bool_exp
  ) {
    eatfresh_article(where: $where, order_by: { name: asc }) {
      id
      ean
      name
    }
    eatfresh_article_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

// Deferred per-article rfid_tag counts. Fired after the article list renders
// so the table is visible immediately; the count gates the delete button,
// which only matters once the user actually clicks it.
export const ADMIN_ARTICLE_RFID_COUNTS_QUERY = graphql(`
  query AdminArticleRfidCounts($article_ids: [uuid!]!) {
    eatfresh_article(where: { id: { _in: $article_ids } }) {
      id
      rfid_tags_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`);

export const ARTICLE_IDS_IN_ASSIGNED_FRIDGE_LAYOUTS_QUERY = graphql(`
  query ArticleIdsInAssignedFridgeLayouts {
    eatfresh_fridge_layout_level_position(
      where: {
        fridge_layout_level: {
          fridge_layout: {
            fridge_layout_assignments: {
              status: { _in: [REQUESTED, ACTIVE] }
              deprecated_at: { _is_null: true }
            }
          }
        }
      }
    ) {
      article_id
    }
  }
`);

// FIXME: This gets ALL the fridges, this may become a problem if we have a lot of fridges.
export const FRIDGES_MAP_QUERY = graphql(`
  query FridgesMap {
    eatfresh_fridge(where: { type: { _in: [DEMO, SELFLY] } }) {
      id
      given_id
      type
      place {
        ...Place
      }
      fridge_stocks {
        quantity
        rfid_tag {
          id
          article {
            uploaded_file {
              url
            }
          }
        }
      }
    }
  }
`);

export const FRIDGES_MAP_PINS_QUERY = graphql(`
  query FridgesMapPins {
    eatfresh_fridge(
      where: {
        type: { _in: [DEMO, SELFLY] }
        connection_status: { _eq: ONLINE }
        disabled: { _eq: false }
      }
    ) {
      id
      given_id
      place {
        id
        name
        latitude
        longitude
      }
    }
  }
`);

export const FRIDGES_STOCK_QUERY = graphql(`
  query FridgesStock($given_ids: [String!]!) {
    eatfresh_fridge(where: { given_id: { _in: $given_ids } }) {
      id
      given_id
      asset_placements {
        id
        rfid_asset {
          id
          rfid_tags {
            id
            article {
              name
              id
              uploaded_file {
                url
              }
            }
          }
        }
      }
      fridge_layout_assignments(
        where: { status: { _eq: ACTIVE } }
        order_by: { created_at: desc }
        limit: 1
      ) {
        fridge_layout {
          fridge_layout_levels {
            order_index
            fridge_layout_level_positions {
              order_index
              article {
                id
              }
            }
          }
        }
      }
    }
  }
`);

export const UNSTORED_RFIDS_QUERY = graphql(`
  query UnstoredRfids {
    eatfresh_rfid_tag(where: { rfid_status: { _eq: UNSTORED } }) {
      ...Rfid
    }
  }
`);

export const ADMIN_VAT_CATEGORIES_QUERY = graphql(`
  query AdminVatCategories(
    $limit: Int!
    $offset: Int!
    $order_by: [eatfresh_vat_category_order_by!] = { name: asc }
    $where: eatfresh_vat_category_bool_exp
  ) {
    eatfresh_vat_category(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      ...VatCategory
    }
    eatfresh_vat_category_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const ADMIN_FRIDGES_QUERY = graphql(`
  query AdminFridges(
    $limit: Int!
    $offset: Int!
    $order_by: [eatfresh_fridge_order_by!] = { given_id: asc }
    $where: eatfresh_fridge_bool_exp
  ) {
    eatfresh_fridge(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      ...Fridge
      asset_placements_aggregate {
        aggregate {
          count
        }
      }
    }
    eatfresh_fridge_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const SELECT_VAT_CATEGORIES_QUERY = graphql(`
  query SelectVatCategories {
    eatfresh_vat_category {
      ...VatCategory
    }
  }
`);
export const PASSWORD_RESET_TOKEN_QUERY = graphql(`
  query PasswordResetToken($token: String!) {
    eatfresh_password_reset_token(where: { token: { _eq: $token } }) {
      ...PasswordResetToken
    }
  }
`);
export const ARTICLE_BY_PK = graphql(`
  query ARTICLE_BY_PK($articleId: uuid!) {
    eatfresh_article_by_pk(id: $articleId) {
      ...Article
    }
  }
`);
export const ARTICLE_WITH_DETAILS_QUERY = graphql(`
  query ArticleWithDetails($articleId: uuid!) {
    eatfresh_article_by_pk(id: $articleId) {
      ...Article
      article_detail {
        ...ArticleDetails
      }
    }
  }
`);

export const RFID_SCANNERS_QUERY = graphql(`
  query RfidScanners {
    eatfresh_rfid_scanner {
      ...RfidScanner
    }
  }
`);

export const RFID_SCANNERS_ADMIN_QUERY = graphql(`
  query RfidScannersAdmin(
    $where: eatfresh_rfid_scanner_bool_exp
    $limit: Int!
    $offset: Int!
  ) {
    eatfresh_rfid_scanner(where: $where, limit: $limit, offset: $offset) {
      ...RfidScannerAdmin
    }
    eatfresh_rfid_scanner_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const ARTICLE_QUERY = graphql(`
  query Article($where: eatfresh_article_bool_exp!) {
    eatfresh_article(where: $where) {
      ...Article
    }
  }
`);

export const ARTICLE_BY_EAN_QUERY = graphql(`
  query ArticleByEan($ean: String!) {
    eatfresh_article(
      where: { _or: [{ ean: { _ilike: $ean } }, { name: { _ilike: $ean } }] }
    ) {
      ...Article
    }
  }
`);

export const RFID_BOXES_QUERY = graphql(`
  query RfidBoxes {
    eatfresh_rfid_box {
      ...RfidBox
      rfid_asset {
        ...RfidAsset
      }
    }
  }
`);

export const WAREHOUSE_QUERY = graphql(`
  query Warehouses {
    eatfresh_warehouse {
      ...Warehouse
    }
  }
`);

export const WAREHOUSE_ORDER_NAMES_BY_IDS_QUERY = graphql(`
  query WarehouseOrderNamesByIds($ids: [uuid!]!) {
    eatfresh_warehouse_order(where: { id: { _in: $ids } }) {
      id
      name
      warehouse {
        id
        name
      }
    }
  }
`);

export const ADMIN_WAREHOUSE_QUERY = graphql(`
  query AdminWarehouses(
    $where: eatfresh_warehouse_bool_exp!
    $limit: Int!
    $offset: Int!
  ) {
    eatfresh_warehouse(
      where: $where
      order_by: { name: asc }
      limit: $limit
      offset: $offset
    ) {
      ...Warehouse
    }
    eatfresh_warehouse_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const WAREHOUSE_ORDER_QUERY = graphql(`
  query WarehouseOrder {
    eatfresh_warehouse_order(where: { source_warehouse_id: { _is_null: true } }) {
      ...WarehouseOrder
      warehouse_order_receipt {
        uploaded_file {
          url
        }
      }
    }
  }
`);

export const WAREHOUSE_ORDER_POSITIONS_QUERY = graphql(`
  query WarehouseOrderPositionsByPk($orderId: uuid!) {
    eatfresh_warehouse_order_by_pk(id: $orderId) {
      ...WarehouseOrder
      warehouse_order_receipt {
        id
      }
      warehouse_order_positions_aggregate {
        aggregate {
          sum {
            total_articles
          }
        }
      }
      warehouse_order_positions {
        article_id
        created_at
        id
        packaging_units
        boxes
        items_per_pu
        total_articles
        purchase_price
        updated_at
        warehouse_order_id
        article {
          name
          active_for_retail
          active_for_wholesale
          uploaded_file {
            url
          }
        }
      }
    }
  }
`);

/** Shelf-life min + all tags on this order (for per-article labeled counts). */
export const ORDER_RFID_TAGS_QUERY = graphql(`
  query OrderRfidTags($orderId: uuid!) {
    shelf_aggregate: eatfresh_rfid_tag_aggregate(
      where: {
        _or: [
          { wholesale_order_id: { _eq: $orderId } }
          { external_warehouse_order_id: { _eq: $orderId } }
          { warehouse_order_id: { _eq: $orderId } }
        ]
        best_before_date: { _is_null: false }
      }
    ) {
      aggregate {
        min {
          best_before_date
        }
      }
    }
    order_rfid_tags: eatfresh_rfid_tag(
      where: {
        _or: [
          { wholesale_order_id: { _eq: $orderId } }
          { external_warehouse_order_id: { _eq: $orderId } }
          { warehouse_order_id: { _eq: $orderId } }
        ]
      }
    ) {
      id
      article_id
    }
  }
`);

export const ADMIN_WAREHOUSE_ORDER_IDS_QUERY = graphql(`
  query AdminWarehouseOrderIds($where: eatfresh_warehouse_order_bool_exp!) {
    eatfresh_warehouse_order(where: $where, order_by: { created_at: desc }) {
      id
    }
  }
`);

export const ADMIN_WAREHOUSE_ORDER_QUERY = graphql(`
  query AdminWarehouseOrders($supplier: String!, $limit: Int!, $offset: Int!) {
    eatfresh_warehouse_order(
      where: { supplier: { _ilike: $supplier } }
      order_by: { supplier: asc }
      limit: $limit
      offset: $offset
    ) {
      ...WarehouseOrder
    }
    eatfresh_warehouse_order_aggregate(
      where: { supplier: { _ilike: $supplier } }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const ADMIN_WAREHOUSE_ORDER_BY_PK_QUERY = graphql(`
  query AdminWarehouseOrderByPk($id: uuid!, $offset: Int!, $pageSize: Int!) {
    eatfresh_warehouse_order_by_pk(id: $id) {
      ...WarehouseOrder
      warehouse {
        name
      }
      source_warehouse {
        name
      }
      warehouse_order_positions(offset: $offset, limit: $pageSize) {
        id
        article {
          name
          id
        }
        packaging_units
        boxes
        items_per_pu
        total_articles
        purchase_price
      }
    }

    eatfresh_warehouse_order_position_aggregate(
      where: { warehouse_order_id: { _eq: $id } }
    ) {
      aggregate {
        count
      }
    }
  }
`);

/** Minimal warehouse order row for status dropdown + place / request-transport actions */
export const WAREHOUSE_ORDER_STATUS_HEADER_QUERY = graphql(`
  query WarehouseOrderStatusHeader($id: uuid!) {
    eatfresh_warehouse_order_by_pk(id: $id) {
      id
      warehouse_order_status
      warehouse {
        name
      }
      source_warehouse {
        name
      }
    }
  }
`);

export const ADMIN_WAREHOUSE_ORDER_QUERY_POSITIONS_NEW = graphql(`
  query ADMIN_WAREHOUSE_ORDER_QUERY_POSITIONS_NEW(
    $offset: Int!
    $pageSize: Int!
    $before: timestamp
    $after: timestamp
    $selectedStatuses: [eatfresh_warehouse_order_status_enum!]!
  ) {
    eatfresh_warehouse_order(
      where: {
        created_at: { _lt: $before, _gt: $after }
        warehouse_order_status: { _in: $selectedStatuses }
      }
      order_by: { created_at: desc }
      limit: $pageSize
      offset: $offset
    ) {
      ...WarehouseOrder
      source_warehouse {
        id
        name
      }
      warehouse {
        id
        name
        external
      }
    }
    eatfresh_warehouse_order_aggregate(
      where: {
        created_at: { _lt: $before, _gt: $after }
        warehouse_order_status: { _in: $selectedStatuses }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const LABEL_WAREHOUSE_ORDER_RECEIPT_POSITIONS = graphql(`
  query LabelWarehouseOrderReceiptPositions {
    eatfresh_warehouse_order_receipt(
      where: {
        warehouse_order: { warehouse_order_status: { _eq: DELIVERED } }
        warehouse_order_receipt_positions: { is_labeled: { _eq: false } }
      }
    ) {
      id
      warehouse_order {
        ...WarehouseOrder
      }
      warehouse_order_receipt_positions(where: { is_labeled: { _eq: false } }) {
        ...WarehouseOrderReceiptPosition
        article_labeling_processes {
          box_content_plans {
            id
            status
            rfid_tags_aggregate {
              aggregate {
                count
              }
            }
          }
          status
          id
        }
      }
    }
  }
`);

export const WAREHOUSE_ORDER_RECEIPT_POSITIONS_QUERY = graphql(`
  query WarehouseOrderReceiptPositions(
    $where: eatfresh_warehouse_order_receipt_position_bool_exp!
  ) {
    eatfresh_warehouse_order_receipt_position(where: $where) {
      ...WarehouseOrderReceiptPosition
    }
  }
`);

export const SCANNER_EXISTS_BY_PK = graphql(`
  query SCANNER_EXISTS_BY_PK($id: uuid!) {
    eatfresh_rfid_scanner_by_pk(id: $id) {
      id
      given_id
      type
    }
  }
`);
export const SCANNER_EXISTS_BY_GIVEN_ID = graphql(`
  query SCANNER_EXISTS_BY_GIVEN_ID($givenId: String!) {
    eatfresh_rfid_scanner(where: { given_id: { _eq: $givenId } }) {
      id
      given_id
      type
    }
  }
`);

export const ADMIN_FRIDGE_TARGET_STOCK_QUERY = graphql(`
  query AdminFridgeTargetStock($limit: Int, $offset: Int, $fridgeId: uuid!) {
    eatfresh_fridge_by_pk(id: $fridgeId) {
      ...Fridge
      fridge_target_stocks(where: { deprecated_at: { _is_null: true } }) {
        ...FridgeTargetStock
        fridge_target_stock_positions(limit: $limit, offset: $offset) {
          ...FridgeTargetStockPosition
          article {
            ...Article
          }
        }

        fridge_target_stock_positions_aggregate {
          aggregate {
            count
          }
        }
      }
    }
  }
`);

export const SUPPLIER_FRIDGE_ORDER_QUERY = graphql(`
  query AdminFridgeOrder($fridgeOrderId: uuid!) {
    eatfresh_fridge_order_by_pk(id: $fridgeOrderId) {
      ...FridgeOrder
      fridge {
        ...Fridge
      }
      box_content_plans(where: { status: { _eq: GENERATED } }) {
        ...BoxContentPlanWithBox
      }
    }
  }
`);

export const ADMIN_PLACES_QUERY = graphql(`
  query AdminPlaces($limit: Int!, $offset: Int!) {
    eatfresh_place(limit: $limit, offset: $offset, order_by: { name: asc }) {
      ...Place

      warehouses_aggregate {
        aggregate {
          count
        }
      }
      fridges_aggregate {
        aggregate {
          count
        }
      }
    }
    eatfresh_place_aggregate {
      aggregate {
        count
      }
    }
  }
`);

export const ADMIN_PLACE_BY_NAME_QUERY = graphql(`
  query AdminPlaceByName($name: String!) {
    eatfresh_place(where: { name: { _ilike: $name } }) {
      ...Place
    }
  }
`);

export const REFILLER_FRIDGES_IN_PLACE_QUERY = graphql(`
  query RefillerFridgesInPlace($place_id: uuid!) {
    eatfresh_place_by_pk(id: $place_id) {
      ...Place
      fridges {
        ...Fridge
        fridge_order {
          id
          box_content_plans(where: { status: { _eq: PICKED_UP } }) {
            id
          }
        }
      }
    }
  }
`);

export const PENDING_RECEIVED_WAREHOUSE_ORDERS_AGGREGATE = graphql(`
  query PENDING_RECEIVED_WAREHOUSE_ORDERS_AGGREGATE {
    eatfresh_warehouse_order(
      where: {
        warehouse_order_status: { _eq: DRAFT }
        source_warehouse_id: { _is_null: true }
      }
    ) {
      id
    }
  }
`);

export const EXTERNAL_WAREHOUSE_ORDERS_QUERY = graphql(`
  query ExternalWarehouseOrders($source_warehouse_id: uuid!) {
    eatfresh_warehouse_order(
      where: {
        source_warehouse_id: { _eq: $source_warehouse_id }
        warehouse_order_status: { _in: [PLACED, READY_TO_SHIP, TRANSPORT_REQUESTED] }
      }
      order_by: { created_at: desc }
    ) {
      ...WarehouseOrder
      warehouse {
        id
        name
        external
      }
      source_warehouse {
        id
        name
      }
      warehouse_order_positions {
        id
        article_id
        packaging_units
        boxes
        items_per_pu
        total_articles
        article {
          id
          name
          ean
        }
      }
    }
  }
`);

/** Draft external order for source → destination (info banner on create form only). */
export const DRAFT_EXTERNAL_ORDER_FOR_ROUTE_QUERY = graphql(`
  query DraftExternalOrderForRoute(
    $source_warehouse_id: uuid!
    $destination_warehouse_id: uuid!
  ) {
    eatfresh_warehouse_order(
      where: {
        source_warehouse_id: { _eq: $source_warehouse_id }
        warehouse_id: { _eq: $destination_warehouse_id }
        warehouse_order_status: { _eq: DRAFT }
      }
      order_by: { created_at: asc }
      limit: 1
    ) {
      id
      created_at
      warehouse_order_status
      warehouse_order_positions_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`);

export const EXTERNAL_WAREHOUSE_ORDERS_SHIPPED_QUERY = graphql(`
  query ExternalWarehouseOrdersShipped($source_warehouse_id: uuid!) {
    eatfresh_warehouse_order(
      where: {
        source_warehouse_id: { _eq: $source_warehouse_id }
        warehouse_order_status: { _eq: IN_TRANSIT }
      }
      order_by: { updated_at: desc }
    ) {
      ...WarehouseOrder
      shipped_at
      logistics_order_id
      warehouse {
        id
        name
        external
      }
      source_warehouse {
        id
        name
      }
      warehouse_order_positions {
        id
        article_id
        packaging_units
        boxes
        items_per_pu
        total_articles
        article {
          id
          name
        }
      }
    }
  }
`);

export const WAREHOUSE_ORDER_RECEIPT_POSITIONS_WHERE_ITEMS_PER_PACKAGE_UNIT_IS_NULL =
  graphql(`
    query WAREHOUSE_ORDER_RECEIPT_POSITIONS_WHERE_ITEMS_PER_PACKAGE_UNIT_IS_NULL(
      $warehouse_order_id: uuid!
    ) {
      eatfresh_warehouse_order_receipt_position(
        where: {
          article: {
            article_detail: {
              _or: [
                { items_per_package_unit: { _is_null: true } }
                { storage_box_capacity: { _is_null: true } }
              ]
            }
          }
          warehouse_order_receipt: {
            warehouse_order_id: { _eq: $warehouse_order_id }
          }
        }
      ) {
        article_id
      }
    }
  `);

export const WAREHOUSE_ARTICLES_BY_ID_ARRAY = graphql(`
  query WAREHOUSE_ARTICLES_BY_ID_ARRAY($idArray: [uuid!]!) {
    eatfresh_article(where: { id: { _in: $idArray } }) {
      ...Article
      article_detail {
        ...ArticleDetails
      }
    }
  }
`);

/**
 * Same `article_detail` shape as {@link ADMIN_ARTICLES_QUERY} (incl. `wholesale_price`).
 * Use only from admin UI; actual access is still enforced by Hasura `ADMIN` role.
 */
export const ADMIN_ARTICLES_BY_ID_ARRAY = graphql(`
  query AdminArticlesByIdArray($idArray: [uuid!]!) {
    eatfresh_article(where: { id: { _in: $idArray } }) {
      ...Article
      article_detail {
        ...ArticleDetails
        wholesale_price
      }
    }
  }
`);

export const WAREHOUSE_ORDER_RECEIPT_POSITION_BY_ID = graphql(`
  query WAREHOUSE_ORDER_RECEIPT_POSITION_BY_ID($id: uuid!) {
    eatfresh_warehouse_order_receipt_position_by_pk(id: $id) {
      ...WarehouseOrderReceiptPosition
    }
  }
`);

export const LOGISTICS_PROBLEM_QUERY = graphql(`
  query LogisticsProblem($id: uuid!) {
    eatfresh_logistics_problem_by_pk(id: $id) {
      ...LogisticsProblem
    }
  }
`);

export const EASY_REFILL_PERMISSIONS_QUERY = graphql(`
  query EasyRefillPermissions(
    $limit: Int!
    $offset: Int!
    $where: eatfresh_easy_refill_permission_bool_exp!
    $order_by: [eatfresh_easy_refill_permission_order_by!] = {
      created_at: desc
    }
  ) {
    eatfresh_easy_refill_permission(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      id
      fridge_id
      created_at
      fridge {
        ...Fridge
      }
    }
    eatfresh_easy_refill_permission_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const WAREHOUSE_TYPES_QUERY = graphql(`
  query WarehouseTypes {
    eatfresh_warehouse_order_type {
      ...WarehouseOrderType
    }
  }
`);

export const ADMIN_B2B_CUSTOMERS_QUERY = graphql(`
  query AdminB2bCustomers(
    $limit: Int!
    $offset: Int!
    $order_by: [eatfresh_b2b_customer_order_by!] = { name: asc }
    $where: eatfresh_b2b_customer_bool_exp
  ) {
    eatfresh_b2b_customer(
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      ...B2bCustomer
    }
    eatfresh_b2b_customer_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const B2B_CUSTOMERS_SELECT_QUERY = graphql(`
  query B2bCustomersSelect {
    eatfresh_b2b_customer(order_by: { name: asc }) {
      id
      name
    }
  }
`);

export const MY_WAREHOUSE_ASSIGNMENT_QUERY = graphql(`
  query MyWarehouseAssignment {
    eatfresh_warehouse_assignment {
      id
      enforce_quick_labeling
      warehouse_id
    }
  }
`);
